import React, { useState, useEffect, useCallback } from 'react';
import { getAgents, createAgent, updateAgent, deleteAgent } from '../services/api';
import './AgentsManager.css';

const AgentsManager = ({ onClose, onAgentsChange, standalone = false, initialAgents = [] }) => {
  // Usar os agentes iniciais que vêm como prop, se disponíveis
  const [agents, setAgents] = useState(initialAgents);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    available: true
  });

  // Handler para tecla ESC fechar o modal
  const handleKeyDown = useCallback((event) => {
    if (!standalone && event.key === 'Escape' && onClose) {
      onClose();
    }
  }, [standalone, onClose]);

  // Inicializar com dados provindos do pai se disponíveis
  useEffect(() => {
    // Se não recebemos agentes iniciais e estamos no modo standalone, buscar do backend
    if (initialAgents.length === 0 && standalone) {
      console.log('AgentsManager: Buscando agentes do backend');
      fetchAgents();
    }

    // Adicionar event listener para tecla ESC
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup: remover event listener
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [initialAgents.length, standalone, handleKeyDown]); // Adicionando dependências corretas

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('AgentsManager: Fazendo requisição para buscar agentes do backend');
      const response = await getAgents();
      
      if (response.success) {
        console.log('AgentsManager: Agentes recebidos do backend:', response.agents?.length || 0);
        
        // Verificar se é necessário atualizar o estado
        const agentsChanged = JSON.stringify(response.agents) !== JSON.stringify(agents);
        if (agentsChanged) {
          setAgents(response.agents);
          
          // Notificar o componente pai sobre os agentes carregados
          if (onAgentsChange && response.agents?.length > 0) {
            console.log('AgentsManager: Notificando o App sobre os agentes carregados');
            onAgentsChange(response.agents);
          }
        } else {
          console.log('AgentsManager: Agentes não mudaram, ignorando atualização');
        }
      } else {
        setError('Falha ao carregar agentes');
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value, 10) : value
    });
  };

  const handleSelectAgent = (agent) => {
    setSelectedAgent(agent);
    setFormData({
      name: agent.name,
      available: agent.available !== false
    });
  };

  const handleNewAgent = () => {
    setSelectedAgent(null);
    setFormData({
      name: '',
      available: true
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      if (selectedAgent) {
        // Atualizar agente existente
        const response = await updateAgent(selectedAgent.id, formData);
        
        if (response.success) {
          // Atualizar lista local
          const updatedAgents = agents.map(agent => 
            agent.id === selectedAgent.id ? { ...response.agent } : agent
          );
          setAgents(updatedAgents);
          
          // Notificar o componente pai sobre a mudança
          if (onAgentsChange) {
            onAgentsChange(updatedAgents);
          }
        } else {
          setError('Falha ao atualizar agente');
        }
      } else {
        // Criar novo agente
        const response = await createAgent(formData);
        
        if (response.success) {
          // Adicionar à lista local
          const updatedAgents = [...agents, response.agent];
          setAgents(updatedAgents);
          
          // Notificar o componente pai sobre a mudança
          if (onAgentsChange) {
            onAgentsChange(updatedAgents);
          }
        } else {
          setError('Falha ao criar agente');
        }
      }
      
      // Limpar formulário
      handleNewAgent();
    } catch (err) {
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (agentId) => {
    if (!window.confirm('Tem certeza que deseja excluir este agente?')) {
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await deleteAgent(agentId);
      
      if (response.success) {
        // Remover da lista local
        const updatedAgents = agents.filter(agent => agent.id !== agentId);
        setAgents(updatedAgents);
        
        // Notificar o componente pai sobre a mudança
        if (onAgentsChange) {
          onAgentsChange(updatedAgents);
        }
        
        // Se o agente excluído estiver selecionado, limpar seleção
        if (selectedAgent && selectedAgent.id === agentId) {
          handleNewAgent();
        }
      } else {
        setError('Falha ao excluir agente');
      }
    } catch (err) {
      setError(`Erro: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <>
      {error && <div className="error-message">{error}</div>}
      
      <div className={`agents-manager-content ${standalone ? 'standalone' : ''}`}>
        <div className="agent-form">
          <h3>{selectedAgent ? 'Editar Agente' : 'Novo Agente'}</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Nome:</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                disabled={loading}
              />
            </div>
            
            
            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  name="available"
                  checked={formData.available}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                Disponível
              </label>
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={loading}
            >
              {selectedAgent ? 'Atualizar' : 'Criar'}
            </button>
          </form>
        </div>
        
        <div className="agents-list">
          <h3>Agentes Cadastrados</h3>
          
          {loading && <div className="loading-spinner">Carregando...</div>}
          
          {agents.length === 0 ? (
            <div className="empty-state">
              <div className="empty-message">
                <p>Nenhum agente cadastrado.</p>
                <p>Utilize o formulário ao lado para adicionar seu primeiro agente.</p>
                <p>Agentes marcados como disponíveis aparecerão na tela de otimização.</p>
              </div>
            </div>
          ) : (
            <ul>
              {agents.map(agent => (
                <li 
                  key={agent.id} 
                  className={selectedAgent && selectedAgent.id === agent.id ? 'selected' : ''}
                  onClick={() => handleSelectAgent(agent)}
                >
                  <span className={`agent-status ${agent.available ? 'available' : 'unavailable'}`}></span>
                  <span className="agent-name">{agent.name}</span>
                  {selectedAgent && selectedAgent.id === agent.id && 
                    <span style={{
                      fontSize: '0.85rem',
                      color: '#666',
                      marginRight: '8px',
                      fontStyle: 'italic'
                    }}>
                      {agent.available ? 'Disponível' : 'Indisponível'}
                    </span>
                  }
                  <button 
                    className="delete-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(agent.id);
                    }}
                    disabled={loading}
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          
          {/* "Novo Agente" button removed as it's redundant - the form already allows creating new agents */}
        </div>
      </div>
    </>
  );

  // Se estiver no modo standalone, retornar apenas o conteúdo
  if (standalone) {
    return content;
  }

  // Caso contrário, envolver em um modal
  return (
    <div className="agents-manager">
      <div className="agents-manager-header">
        <h2>Gerenciamento de Agentes</h2>
        <button className="close-button" onClick={onClose}>×</button>
      </div>
      {content}
    </div>
  );
};

export default AgentsManager;